import { Mutation } from "@apollo/client/react/components";
import { AnchorButton } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import ProfilePicture from "@govlaunch/profile-picture";
import Link from "next/link";
import React, { HTMLAttributes } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { refetchQueries } from "~/lib/apollo/RefetchQueriesFixLink";
import LeaveGroupAsVendor from "~/lib/mutations/LeaveGroupAsVendor";
import RemoveGroup from "~/lib/mutations/RemoveGroup";
import {
  ILeaveGroupAsVendorMutation,
  ILeaveGroupAsVendorMutationVariables,
} from "~/lib/mutations/__generated__/LeaveGroupAsVendor.generated";
import {
  IRemoveGroupMutation,
  IRemoveGroupMutationVariables,
} from "~/lib/mutations/__generated__/RemoveGroup.generated";
import { IVendorQuery } from "~/lib/queries/__generated__/VendorQuery.generated";
import { IGroup } from "~/types/types";
import AnchorIcon from "~/components/icons/AnchorIcon";
import LeaveIcon from "~/components/icons/LeaveIcon";
import PenIcon from "~/components/icons/PenIcon";
import TrashIcon from "~/components/icons/TrashIcon";
import { Margin } from "~/components/spacings";
import Tooltip from "~/components/Tooltip";
import UpgradeAccountBanner from "~/components/vendor/profile/UpgradeAccountBanner";

interface IVendorGroupsProps {
  vendor: NonNullable<IVendorQuery["company"]>;
}
export default function VendorGroups({ vendor }: IVendorGroupsProps) {
  const myGroups = vendor.groups.filter(({ viewerIsOwner }) => viewerIsOwner);
  const otherGroups = vendor.groups.filter(({ viewerIsOwner }) => !viewerIsOwner);

  if (vendor.plan.limits.groups + vendor.plan.limits.groupInvitations === 0) {
    return <UpgradeAccountBanner />;
  }

  if (myGroups.length === 0 && otherGroups.length === 0) {
    return (
      <div>
        <NoGroups vendor={vendor} />
      </div>
    );
  }

  return (
    <div>
      <div>
        <Link
          href="/vendors/[vendorSlug]/[[...content]]"
          as={`/vendors/${vendor.slug}/new-group`}
          passHref={true}
          legacyBehavior={true}
        >
          <AnchorButton
            theme="secondary"
            size="large"
            color={palette.lightestGray}
            textColor={palette.darkGray}
            css={{
              background: "#fff",
              fontSize: 16,
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <PenIcon />
            <span
              css={{
                marginLeft: 4,
              }}
            >
              <FormattedMessage defaultMessage="New Group" id="mx9Iyw" />
            </span>
          </AnchorButton>
        </Link>
      </div>

      {myGroups.length > 0 && (
        <Margin mt={24}>
          <div
            css={{
              background: "#fff",
              border: `1px solid ${palette.lightestGray}`,
              borderRadius: 4,
            }}
          >
            <div
              css={{
                padding: 16,
                fontSize: 24,
                fontWeight: 500,
                borderBottom: `1px solid ${palette.lightestGray}`,
              }}
            >
              <FormattedMessage defaultMessage="My Groups" id="kkghcT" />
            </div>
            <div>
              <GroupListItemDividers>
                {myGroups.map((group) => (
                  <GroupListItem key={group._id} group={group} vendorSlug={vendor.slug} />
                ))}
              </GroupListItemDividers>
            </div>
          </div>
        </Margin>
      )}

      {otherGroups.length > 0 && (
        <Margin mt={24}>
          <div
            css={{
              background: "#fff",
              border: `1px solid ${palette.lightestGray}`,
              borderRadius: 4,
            }}
          >
            <div
              css={{
                padding: 16,
                fontSize: 24,
                fontWeight: 500,
                borderBottom: `1px solid ${palette.lightestGray}`,
              }}
            >
              <FormattedMessage defaultMessage="Other Groups" id="FnO28L" />
            </div>
            <div>
              <GroupListItemDividers>
                {otherGroups.map((group) => (
                  <GroupListItem key={group._id} group={group} />
                ))}
              </GroupListItemDividers>
            </div>
          </div>
        </Margin>
      )}
    </div>
  );
}

function GroupListItemDividers(props: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...props}
      css={{
        "& > :not(:first-of-type)": {
          borderTop: `solid 1px ${palette.lightestGray}`,
        },
      }}
    />
  );
}

interface IGroupRowProps {
  group: Pick<IGroup, "_id" | "slug" | "name" | "location" | "website" | "logo" | "viewerCanDelete" | "viewerCanEdit">;
  vendorSlug?: string | null;
}

function GroupListItem({ group, vendorSlug }: IGroupRowProps) {
  const intl = useIntl();

  return (
    <div
      css={{
        position: "relative",
        display: "flex",
        padding: 16,
      }}
    >
      <Link passHref={true} href="/groups/[groupSlug]" as={`/groups/${group.slug}`} legacyBehavior={true}>
        <a
          css={{
            display: "flex",
          }}
        >
          <ProfilePicture size={80} image={group.logo} name={group.name} />
        </a>
      </Link>

      <div
        css={{
          marginLeft: 12,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          overflow: "hidden",
          paddingRight: 40,
        }}
      >
        <Link passHref={true} href="/groups/[groupSlug]" as={`/groups/${group.slug}`} legacyBehavior={true}>
          <a
            css={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <p
              css={{
                fontWeight: 500,
                margin: 0,
                fontSize: 20,
                letterSpacing: 0.25,
                color: palette.darkGray,
              }}
            >
              {group.name}
            </p>
          </a>
        </Link>

        <a
          target="_blank"
          rel="noreferrer noopener"
          href={group.website}
          css={{
            marginTop: 6,
            fontSize: 16,
            color: palette.mediumGray,
            display: "flex",
            alignItems: "center",
          }}
        >
          <AnchorIcon />
          <span
            css={{
              marginLeft: 4,
            }}
          >
            <FormattedMessage defaultMessage="Website" id="JkLHGw" />
          </span>
        </a>
      </div>

      <div
        css={{
          position: "absolute",
          top: 8,
          right: 8,
          display: "flex",
          alignItems: "center",
        }}
      >
        {group.viewerCanDelete ? (
          <Mutation<IRemoveGroupMutation, IRemoveGroupMutationVariables>
            mutation={RemoveGroup}
            refetchQueries={refetchQueries(["CompanyAccountBillingInfo", "Selfie"])}
            variables={{
              groupId: group._id,
            }}
          >
            {(deleteGroup) => (
              <Tooltip
                backgroundColor={palette.text}
                render={() => (
                  <span
                    css={{
                      display: "block",
                      fontSize: 12,
                      padding: 8,
                      color: palette.white,
                      fontWeight: 500,
                    }}
                  >
                    <FormattedMessage defaultMessage="Delete Group?" id="OjeqDO" />
                  </span>
                )}
              >
                {({ innerRef, bind }) => (
                  <button
                    ref={innerRef}
                    {...bind}
                    onClick={() => {
                      if (
                        window.confirm(
                          intl.formatMessage({
                            defaultMessage: "Are you sure you want to remove this group?",
                            id: "OJ9QmS",
                          }),
                        )
                      ) {
                        deleteGroup();
                      }
                    }}
                    type="button"
                    css={{
                      cursor: "pointer",
                      padding: 0,
                      display: "inline-flex",
                      border: 0,
                      outline: 0,
                      background: "none",
                      marginRight: 8,
                    }}
                  >
                    <TrashIcon />
                  </button>
                )}
              </Tooltip>
            )}
          </Mutation>
        ) : (
          <Tooltip
            backgroundColor={palette.text}
            render={() => (
              <span
                css={{
                  display: "block",
                  fontSize: 12,
                  padding: 8,
                  color: palette.white,
                  fontWeight: 500,
                }}
              >
                <FormattedMessage defaultMessage="Leave Group" id="aN2Gli" />?
              </span>
            )}
          >
            {({ innerRef, bind }) => (
              <Mutation<ILeaveGroupAsVendorMutation, ILeaveGroupAsVendorMutationVariables>
                mutation={LeaveGroupAsVendor}
                variables={{
                  groupId: group._id,
                }}
              >
                {(leaveGroup) => (
                  <button
                    ref={innerRef}
                    {...bind}
                    onClick={() => {
                      if (
                        window.confirm(
                          intl.formatMessage({
                            defaultMessage: "Are you sure you want to leave this group?",
                            id: "5BxgKi",
                          }),
                        )
                      ) {
                        leaveGroup({
                          refetchQueries: refetchQueries(["Vendor", "CompanyAccountBillingInfo"]),
                          awaitRefetchQueries: true,
                        });
                      }
                    }}
                    type="button"
                    css={{
                      cursor: "pointer",
                      padding: 0,
                      display: "inline-flex",
                      border: 0,
                      outline: 0,
                      background: "none",
                    }}
                  >
                    <LeaveIcon />
                  </button>
                )}
              </Mutation>
            )}
          </Tooltip>
        )}

        {group.viewerCanEdit && vendorSlug && (
          <Link
            href="/vendors/[vendorSlug]/groups/[groupSlug]"
            as={`/vendors/${vendorSlug}/groups/${group.slug}`}
            passHref={true}
            legacyBehavior={true}
          >
            <a
              css={{
                display: "inline-flex",
              }}
            >
              <PenIcon />
            </a>
          </Link>
        )}
      </div>
    </div>
  );
}

interface INoGroupsProps {
  vendor: NonNullable<IVendorQuery["company"]>;
}

function NoGroups({ vendor, ...props }: INoGroupsProps & HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      css={{
        borderRadius: 4,
        background: "#fff",
        border: `1px solid ${palette.lightestGray}`,
        padding: 24,
      }}
      {...props}
    >
      <div
        css={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          maxWidth: 400,
          margin: "0 auto",
        }}
      >
        <svg width={60} height={60} viewBox="0 0 62 62">
          <g fill="none" fillRule="evenodd">
            <circle
              cx={29.949}
              cy={29.949}
              r={29.949}
              transform="translate(1 1)"
              fill="#DAEAF6"
              stroke="#005AEE"
              strokeWidth={0.713}
            />
            <path fill="#0091FC" fillRule="nonzero" d="M46.038 21.995H26.965v-3.81h19.073z" />
            <path fill="#0770BF" fillRule="nonzero" d="M30.924 21.995h-3.706v-3.602h3.706z" />
            <path fill="#EAEAEA" fillRule="nonzero" d="M30.924 32.842h-3.706V21.995h3.706z" />
            <path fill="#B0DDFC" fillRule="nonzero" d="M46.038 21.995h-4.41v-3.81h4.41z" />
            <path
              stroke="#2C426B"
              strokeWidth={0.98}
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M36.915 35.651v4.43H25.069l-5.094 5.797-.026-5.797h-2.107V25.425h6.149"
            />
            <path
              stroke="#2C426B"
              strokeWidth={0.98}
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M26.965 32.842H38.81l5.094 5.797.026-5.797h2.107V18.185H26.965zM30.4 22.803h11.879M42.279 25.335H30.4M30.4 28.351h11.879"
            />
          </g>
        </svg>

        <p
          css={{
            margin: "12px 0 0 0",
            color: palette.text,
            fontWeight: 500,
            fontSize: 16,
          }}
        >
          <FormattedMessage defaultMessage="Create a Group" id="sN7tQJ" />
        </p>

        <p
          css={{
            margin: "12px 0 0 0",
            color: palette.darkGray,
            fontSize: 14,
          }}
        >
          <FormattedMessage
            defaultMessage="Engage with government leaders with your own custom branded groups."
            id="pQxfWy"
          />
        </p>

        <Link
          href="/vendors/[vendorSlug]/[[...content]]"
          as={`/vendors/${vendor.slug}/new-group`}
          passHref={true}
          legacyBehavior={true}
        >
          <AnchorButton
            size="small"
            color={palette.innovatorBlue}
            theme="secondary"
            css={{
              margin: "12px 0 0 0",
              minWidth: 150,
            }}
          >
            <FormattedMessage defaultMessage="New Group" id="mx9Iyw" />
          </AnchorButton>
        </Link>
      </div>
    </div>
  );
}
